import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  LayoutGrid,
  Network,
  BookOpen,
  GitBranch,
  Activity,
  Rocket,
  Boxes,
  HeartPulse,
  Bot,
  Lock,
  Lightbulb,
  FileCode,
  Hammer,
  FlaskConical,
  Undo2,
  Sparkles,
  Radar,
  Container,
} from 'lucide-react';

import ProjectOverview     from '../components/lab/ProjectOverview';
import ArchitectureExplorer from '../components/lab/ArchitectureExplorer';
import KnowledgeBase       from '../components/lab/KnowledgeBase';
import VersionHistory      from '../components/lab/VersionHistory';
import EngineeringLogs     from '../components/lab/EngineeringLogs';
import DeploymentHistory   from '../components/lab/DeploymentHistory';
import FeatureInventory    from '../components/lab/FeatureInventory';
import ProductionHealth    from '../components/lab/ProductionHealth';
import LabAssistant        from '../components/lab/LabAssistant';
import EngineeringPlannerSection from '../components/lab/EngineeringPlannerSection';
import CodeDiffViewer      from '../components/lab/CodeDiffViewer';
import PreviewBuilderSection from '../components/lab/PreviewBuilderSection';
import TestRunnerSection   from '../components/lab/TestRunnerSection';
import DeploymentManagerSection from '../components/lab/DeploymentManagerSection';
import RollbackManagerSection from '../components/lab/RollbackManagerSection';
import SelfImprovementSection from '../components/lab/SelfImprovementSection';
import CommandCenterSection from '../components/lab/CommandCenterSection';
import SandboxSection      from '../components/lab/SandboxSection';

/**
 * Lab — AI Engineering Workspace (Phase 51.A).
 *
 * Read-only in preview/development. Production-only write actions
 * (refresh index, add memory) are gated by the backend AND by the UI
 * banner so the operator always knows the current mode.
 */
const SECTIONS = [
  { id: 'command',      label: 'COMMAND CENTER',   icon: Radar,      Comp: CommandCenterSection },
  { id: 'overview',     label: 'OVERVIEW',         icon: LayoutGrid, Comp: ProjectOverview },
  { id: 'health',       label: 'PROD HEALTH',      icon: HeartPulse, Comp: ProductionHealth },
  { id: 'architecture', label: 'ARCHITECTURE',     icon: Network,    Comp: ArchitectureExplorer },
  { id: 'inventory',    label: 'FEATURES',         icon: Boxes,      Comp: FeatureInventory },
  { id: 'planner',      label: 'PLANNER',          icon: Lightbulb,  Comp: EngineeringPlannerSection },
  { id: 'codediffs',    label: 'CODE DIFFS',       icon: FileCode,   Comp: CodeDiffViewer },
  { id: 'preview',      label: 'PREVIEW',          icon: Hammer,     Comp: PreviewBuilderSection },
  { id: 'sandbox',      label: 'SANDBOX',          icon: Container,  Comp: SandboxSection },
  { id: 'tests',        label: 'TESTS',            icon: FlaskConical, Comp: TestRunnerSection },
  { id: 'deploy',       label: 'DEPLOY',           icon: Rocket,     Comp: DeploymentManagerSection },
  { id: 'rollback',     label: 'ROLLBACK',         icon: Undo2,      Comp: RollbackManagerSection },
  { id: 'improve',      label: 'SELF-IMPROVE',     icon: Sparkles,   Comp: SelfImprovementSection },
  { id: 'knowledge',    label: 'KNOWLEDGE BASE',   icon: BookOpen,   Comp: KnowledgeBase },
  { id: 'deployments',  label: 'DEPLOYMENT HIST.', icon: Rocket,     Comp: DeploymentHistory },
  { id: 'versions',     label: 'VERSION HISTORY',  icon: GitBranch,  Comp: VersionHistory },
  { id: 'logs',         label: 'ENG. LOGS',        icon: Activity,   Comp: EngineeringLogs },
  { id: 'assistant',    label: 'AI ASSISTANT',     icon: Bot,        Comp: LabAssistant },
];

export default function Lab() {
  const { token } = useAuth();
  const [env, setEnv] = useState(null);
  const [active, setActive] = useState('command');

  useEffect(() => {
    api.get('/lab/env').then(({ data }) => setEnv(data)).catch(() => {});
  }, []);

  const isProd = env?.write_enabled === true;
  const ActiveComp = SECTIONS.find((s) => s.id === active)?.Comp;

  return (
    <div data-testid="lab-page" className="p-2 md:p-4">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3 pb-3 border-b border-[var(--border-default)]">
        <div>
          <h1 className="text-xl md:text-2xl font-display tracking-[0.3em] uppercase text-cyan-sys glow-cyan">
            LAB
          </h1>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--txt-secondary)]">
            AI ENGINEERING WORKSPACE · ATDE PLATFORM
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase">
          {isProd ? (
            <span data-testid="lab-mode-prod" className="border border-buy text-buy px-2 py-1">
              ● PRODUCTION · WRITE ENABLED
            </span>
          ) : (
            <span data-testid="lab-mode-readonly" className="border border-hold text-hold px-2 py-1 flex items-center gap-1.5">
              <Lock size={11} /> READ-ONLY · PREVIEW
            </span>
          )}
          {env?.version && (
            <span className="border border-[var(--border-default)] text-[var(--txt-secondary)] px-2 py-1">
              BUILD {String(env.version).slice(0, 7)}
            </span>
          )}
        </div>
      </div>

      {/* PREVIEW BANNER */}
      {env && !isProd && (
        <div
          data-testid="lab-readonly-banner"
          className="mb-3 border border-hold text-hold px-3 py-2 text-[11px] tracking-wider"
        >
          ⚠ Lab is read-only in preview mode. Index refresh, memory writes,
          and deployment recording are disabled here. They activate
          automatically when ATDE is deployed to its production domain.
        </div>
      )}

      {/* SECTION TABS */}
      <div className="flex flex-wrap gap-1 mb-3 border-b border-[var(--border-default)]">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            data-testid={`lab-tab-${s.id}`}
            onClick={() => setActive(s.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.2em] uppercase border-b-2 transition-colors
              ${active === s.id
                ? 'border-cyan-sys text-cyan-sys'
                : 'border-transparent text-[var(--txt-secondary)] hover:text-[var(--txt-primary)]'}`}
          >
            <s.icon size={12} />
            <span>{s.label}</span>
          </button>
        ))}
      </div>

      {/* SECTION CONTENT */}
      <div data-testid={`lab-section-${active}`}>
        {ActiveComp && <ActiveComp env={env} isProd={isProd} token={token} onSwitchTab={setActive} />}
      </div>
    </div>
  );
}
